import { ArrowRight, Clock3, UsersRound } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ProviderMark } from "../components";
import { HeroVideo } from "./HeroVideo";
import { NoirBar, NoirFooter } from "./NoirChrome";
import "./MarketingPage.css";

// Illustrative catalog for the public page. One person's loops spread across five agents, which is
// the situation the page is about. Every field maps to something the product actually
// records: name, purpose (description), source, cadence, reliability with its resolved sample,
// tags, the latest reported outcome, the last run signal, and which groups the loop is shared with.
// Nothing here is live data.
type IllustrativeLoop = {
  id: string;
  name: string;
  provider: "Claude" | "Codex" | "Grok" | "Muse" | "dots";
  source: string;
  cadence: string;
  reliability: string;
  resolved: number;
  lastSignal: string;
  purpose: string;
  tags: string[];
  latestResult: string;
  sharedWith: string[];
};

const illustrativeLoops: IllustrativeLoop[] = [
  {
    id: "customer-voice",
    name: "Customer Voice Digest",
    provider: "Claude",
    source: "Claude · Cloudflare Worker",
    cadence: "Daily 6:00 PM",
    reliability: "91%",
    resolved: 22,
    lastSignal: "Today 6:02 PM",
    purpose: "Clusters the day's support and sales conversations into themes and posts a short digest.",
    tags: ["customer", "support", "slack"],
    latestResult: "Billing confusion tied to invoice timing",
    sharedWith: ["Growth"],
  },
  {
    id: "morning-seo",
    name: "Morning SEO Brief",
    provider: "Codex",
    source: "Codex · macOS LaunchAgent",
    cadence: "Weekdays 7:30 AM",
    reliability: "88%",
    resolved: 17,
    lastSignal: "Today 7:34 AM",
    purpose: "Compares overnight search visibility with last week and drafts a brief for the growth channel.",
    tags: ["growth", "seo", "search-console"],
    latestResult: "Three pages gained non-brand search visibility",
    sharedWith: ["Growth"],
  },
  {
    id: "invoice-match",
    name: "Invoice Match",
    provider: "Codex",
    source: "Codex · GitHub Actions",
    cadence: "Daily 11:00 AM",
    reliability: "96%",
    resolved: 52,
    lastSignal: "Today 11:01 AM",
    purpose: "Matches new invoices to purchase records and flags exceptions for finance to review.",
    tags: ["finance", "quickbooks"],
    latestResult: "48 invoices matched; two need review",
    sharedWith: ["Finance Ops"],
  },
  {
    id: "inbox-triage",
    name: "Inbox Triage",
    provider: "Muse",
    source: "Muse · standing task",
    cadence: "Weekdays 8:00 AM",
    reliability: "90%",
    resolved: 20,
    lastSignal: "Today 8:06 AM",
    purpose: "Sorts overnight email into reply, delegate, and archive, and drafts the replies that only need a yes.",
    tags: ["inbox", "personal"],
    latestResult: "Four replies drafted; two threads need a decision",
    sharedWith: [],
  },
  {
    id: "vendor-renewals",
    name: "Vendor Renewals",
    provider: "Grok",
    source: "Grok · Grok Bot",
    cadence: "Mondays 9:00 AM",
    reliability: "86%",
    resolved: 7,
    lastSignal: "Monday 9:12 AM",
    purpose: "Checks contracts renewing in the next 60 days and drafts the cancel-or-keep note for each.",
    tags: ["finance", "contracts"],
    latestResult: "Two renewals flagged before the notice window",
    sharedWith: ["Finance Ops"],
  },
  {
    id: "competitor-watch",
    name: "Competitor Watch",
    provider: "dots",
    source: "dots · cloud computer",
    cadence: "Every 6 hours",
    reliability: "96%",
    resolved: 26,
    lastSignal: "Today 2:14 PM",
    purpose: "Scans named competitor pages for pricing and positioning changes and summarizes what moved.",
    tags: ["market", "pricing"],
    latestResult: "Pricing-page change detected at 2:14 PM",
    sharedWith: [],
  },
];

// A group is a shared workspace. Only loops explicitly granted to it appear there, each with its owner.
const groupLoops = [
  { name: "Customer Voice Digest", provider: "Claude", owner: "Ada Lovelace", initials: "AL", reliability: "91%", resolved: 22, lastSignal: "Today 6:02 PM" },
  { name: "Morning SEO Brief", provider: "Codex", owner: "Ada Lovelace", initials: "AL", reliability: "88%", resolved: 17, lastSignal: "Today 7:34 AM" },
  { name: "Churn Watch", provider: "Codex", owner: "Sam Rivera", initials: "SR", reliability: "95%", resolved: 40, lastSignal: "Today 9:00 AM" },
  { name: "Onboarding Emails", provider: "Claude", owner: "Jo Park", initials: "JP", reliability: "97%", resolved: 63, lastSignal: "Today 8:15 AM" },
] as const;

const cardRhythm = [
  { label: "Tue", succeeded: 2, failed: 0, open: 0 },
  { label: "Wed", succeeded: 3, failed: 0, open: 0 },
  { label: "Thu", succeeded: 4, failed: 0, open: 0 },
  { label: "Fri", succeeded: 4, failed: 0, open: 1 },
  { label: "Sat", succeeded: 3, failed: 1, open: 0 },
  { label: "Sun", succeeded: 5, failed: 0, open: 0 },
  { label: "Mon", succeeded: 2, failed: 1, open: 1 },
] as const;

const cardLoops = [
  { name: "Customer Voice Digest", provider: "Claude", reliability: "91%", resolved: 22 },
  { name: "Morning SEO Brief", provider: "Codex", reliability: "88%", resolved: 17 },
  { name: "Competitor Watch", provider: "dots", reliability: "96%", resolved: 26 },
  { name: "Release Notes Draft", provider: "Codex", reliability: null, resolved: 2 },
] as const;

// One loop's life across three agents. Dates follow the real launches: Grok Bot on Aug 11, dots on Sep 29.
const movePlacements = [
  { provider: "Claude", period: "Jun → Aug", reliability: "91%", resolved: 240, state: "retired" },
  { provider: "Grok", period: "Aug → Sep", reliability: "84%", resolved: 188, state: "retired" },
  { provider: "dots", period: "Sep 30 → now", reliability: "96%", resolved: 26, state: "active" },
] as const;

const moveVersions = [
  { version: "v1", when: "Jun", note: "First recorded definition" },
  { version: "v2", when: "Aug", note: "Adapted for Grok Bot" },
  { version: "v3", when: "Sep", note: "Adapted for dots" },
  { version: "v4", when: "Oct", note: "Added the pricing-page diff" },
] as const;

// The ways one skill reaches an agent. Each row is something that ships today; per-agent packages
// for dots and Muse are not claimed until they exist (docs/POSITIONING.md).
const plugWays = [
  { provider: "claude", agent: "Claude Code", way: "Plugin", detail: "claude plugin install loopgroup" },
  { provider: "codex", agent: "Codex", way: "Skill", detail: "~/.codex/skills/loopgroup" },
  { provider: null, agent: "Any agent that adds an MCP server", way: "Connector", detail: "loopgroup.ai/mcp · sign in to approve" },
  { provider: "other", agent: "Any agent that can call HTTPS", way: "Prompt", detail: "Paste once · reports over HTTPS" },
] as const;

const everyAgent = ["grok", "muse", "dots", "claude", "codex"] as const;

const detailRows = [
  { key: "purpose", label: "Purpose" },
  { key: "source", label: "Runs on" },
  { key: "tags", label: "Tags" },
  { key: "health", label: "Health" },
  { key: "result", label: "Latest result" },
  { key: "shared", label: "Visible in" },
] as const;

export function MarketingPage() {
  const [selectedId, setSelectedId] = useState(illustrativeLoops[0].id);
  const selectedLoop = illustrativeLoops.find((loop) => loop.id === selectedId) ?? illustrativeLoops[0];

  const detailValue = (key: (typeof detailRows)[number]["key"]) => {
    switch (key) {
      case "purpose":
        return selectedLoop.purpose;
      case "source":
        return selectedLoop.source;
      case "tags":
        return selectedLoop.tags.join(" · ");
      case "health":
        return `${selectedLoop.reliability} success · ${selectedLoop.resolved} resolved · 30 days`;
      case "result":
        return selectedLoop.latestResult;
      case "shared":
        return selectedLoop.sharedWith.length ? `${selectedLoop.sharedWith.join(" · ")} · shared by you` : "Only you";
    }
  };

  return (
    <div className="noir">
      <NoirBar>
        <a className="noir__navlink" href="#plug">Plug in</a>
        <a className="noir__navlink" href="#loops">Your loops</a>
        <a className="noir__navlink" href="#move">Move</a>
        <a className="noir__navlink" href="#groups">Groups</a>
        <a className="noir__navlink" href="#share">Share</a>
      </NoirBar>

      <main>
        <section className="noir__hero" aria-labelledby="marketing-title">
          <div className="noir__backdrop" aria-hidden="true"><HeroVideo className="noir__video" src="/media/hero-loop.mp4" poster="/media/hero-poster.jpg" /></div>
          <div className="noir__hero-copy">
          <p className="noir__setup">Grok Bot. Muse. dots. Claude. Codex.</p>
          <h1 id="marketing-title" className="noir__display">Use any agent. Keep your loops.</h1>
          <div className="noir__hero-actions">
            <Link className="noir__cta" to="/start">Plug in your first agent <ArrowRight size={18} aria-hidden="true" /></Link>
            <p>One skill for every agent you use. It starts with a scan: nothing is edited or scheduled.</p>
          </div>
          </div>
        </section>

        <section className="noir__choice" aria-labelledby="choice-title">
          <p className="noir__eyebrow">The choice</p>
          <h2 id="choice-title" className="noir__statement"><strong>Every lab has a personal agent now, and each one wants your routines.</strong> Whichever you pick keeps your loops, how they went, and what they need on its own computer. Switching means starting over. Using two means nobody sees the whole picture.</h2>
        </section>

        <section className="noir__how" id="how" aria-label="How it works">
          <article>
            <p className="noir__eyebrow">01 · Plug in</p>
            <h2 className="noir__statement"><strong>One skill in every agent you use.</strong> It reads what each agent already runs for you and sends only what a loop is. Nothing in the agent is edited.</h2>
          </article>
          <article>
            <p className="noir__eyebrow">02 · See</p>
            <h2 className="noir__statement"><strong>What every agent does for you, in one ledger.</strong> What ran, on which agent, how reliably, with the sample size beside every number.</h2>
          </article>
          <article>
            <p className="noir__eyebrow">03 · Move</p>
            <h2 className="noir__statement"><strong>Move a loop to a better agent with one prompt.</strong> Its history comes along, and moving back is the same prompt.</h2>
          </article>
        </section>

        <section className="noir__plug" id="plug" aria-labelledby="plug-title">
          <div className="noir__section-head">
            <p className="noir__eyebrow">Plug in</p>
            <h2 id="plug-title" className="noir__statement"><strong>The same skill, wherever your agent lives.</strong> Scan, report, categorize, and hand off mean the same thing in every agent, so a loop reads the same wherever it runs.</h2>
          </div>
          <div className="noir__move-grid">
            <div className="noir__board" aria-label="Ways to plug LoopGroup into an agent">
              <header>
                <span className="noir__plug-marks" aria-hidden="true">{everyAgent.map((provider) => <ProviderMark key={provider} provider={provider} size={30} />)}</span>
                <span>One skill · four ways in</span>
              </header>
              <ul className="noir__plug-ways">
                {plugWays.map((entry) => (
                  <li key={entry.way}>
                    <ProviderMark provider={entry.provider} size={30} />
                    <strong>{entry.agent}</strong>
                    <em>{entry.way}</em>
                    <code>{entry.detail}</code>
                  </li>
                ))}
              </ul>
              <footer><span>Scan · report · categorize · hand off</span><span>Same prompts in every agent</span></footer>
            </div>
            <dl className="noir__facts">
              <div><dt>Scan only to start</dt><dd>The first prompt reads what the agent already runs and sends only what a loop is: its name, cadence, and what it needs. Nothing is edited, installed in the agent, or scheduled until you say so.</dd></div>
              <div><dt>One key per agent</dt><dd>Each agent gets its own scoped key or connector sign-in. Revoke one agent without touching the others. Secret values never travel.</dd></div>
              <div><dt>Every agent, by name</dt><dd>Runs from Grok Bot, Muse, dots, Claude, and Codex land on the right agent with its mark, so the ledger always says who did the work.</dd></div>
              <div><dt>No favourite</dt><dd>LoopGroup has no agent of its own and no stake in which one you use. It keeps the record that makes the choice reversible.</dd></div>
            </dl>
          </div>
        </section>

        <section className="noir__ledger" id="loops" aria-labelledby="ledger-title">
          <div className="noir__section-head">
            <p className="noir__eyebrow">See</p>
            <h2 id="ledger-title" className="noir__statement"><strong>What each agent does for you, in one place.</strong> What runs, on which agent, how reliably, and what it produced last.</h2>
          </div>

          <div className="noir__panel">
            <div className="noir__list">
              <div className="noir__cols" aria-hidden="true">
                <span>Loop</span><span>Runs on</span><span>Cadence</span><span>Health</span><span>Last signal</span>
              </div>
              <div aria-label="Illustrative loop catalog">
                {illustrativeLoops.map((loop) => (
                  <button
                    type="button"
                    className={`noir__row${loop.id === selectedLoop.id ? " is-selected" : ""}`}
                    key={loop.id}
                    onClick={() => setSelectedId(loop.id)}
                    aria-pressed={loop.id === selectedLoop.id}
                  >
                    <span className="noir__row-name">
                      <ProviderMark provider={loop.provider} size={32} />
                      <strong>{loop.name}</strong>
                      {loop.sharedWith.length > 0 && <small className="noir__shared" aria-label={`Shared with ${loop.sharedWith.join(", ")}`}><UsersRound size={10} aria-hidden="true" />{loop.sharedWith[0]}</small>}
                    </span>
                    <span>{loop.source.split(" · ")[1] ?? loop.source}</span>
                    <span><Clock3 size={12} aria-hidden="true" />{loop.cadence}</span>
                    <span className="noir__health"><i aria-hidden="true" />{loop.reliability}<small> · {loop.resolved}</small></span>
                    <span className="noir__signal">{loop.lastSignal}</span>
                  </button>
                ))}
              </div>
            </div>

            <article className="noir__detail" aria-live="polite">
              <header>
                <ProviderMark provider={selectedLoop.provider} size={44} />
                <div>
                  <h3>{selectedLoop.name}</h3>
                  <p>{selectedLoop.cadence} <span className="noir__evidence">Enough evidence · {selectedLoop.resolved} resolved</span></p>
                </div>
              </header>
              <dl>
                {detailRows.map(({ key, label }) => (
                  <div key={key}><dt>{label}</dt><dd>{detailValue(key)}</dd></div>
                ))}
              </dl>
              <footer>
                <Link className="noir__cta noir__cta--small" to="/signup"><UsersRound size={15} aria-hidden="true" /> {selectedLoop.sharedWith.length ? "Share with another group" : "Share with your team"}</Link>
                <Link className="noir__navlink" to="/signin">View full loop <ArrowRight size={15} aria-hidden="true" /></Link>
              </footer>
            </article>
          </div>
          <p className="noir__note">Illustrative example · not live data</p>
        </section>

        <section className="noir__move" id="move" aria-labelledby="move-title">
          <div className="noir__section-head">
            <p className="noir__eyebrow">Move</p>
            <h2 id="move-title" className="noir__statement"><strong>A loop belongs to you, not to the agent.</strong> When a better agent ships, move the loop with one handoff prompt. Its history, reliability, notes, and definition come along.</h2>
          </div>
          <div className="noir__move-grid">
            <div className="noir__board" aria-label="Illustrative loop history across three agents">
              <header>
                <span className="noir__row-name"><ProviderMark provider="dots" size={30} /><strong>Competitor Watch</strong></span>
                <span>Same loop · 3 homes · 454 resolved runs · needs 4</span>
              </header>
              <ol className="noir__timeline">
                {movePlacements.map((placement) => (
                  <li className={`noir__placement is-${placement.state}`} key={placement.provider}>
                    <ProviderMark provider={placement.provider} size={34} />
                    <div>
                      <strong>{placement.provider}</strong>
                      <span>{placement.period}</span>
                      <span className="noir__health"><i aria-hidden="true" />{placement.reliability}<small> · {placement.resolved}</small></span>
                    </div>
                    <em>{placement.state === "active" ? "Runs here now" : "Retired"}</em>
                  </li>
                ))}
              </ol>
              <ol className="noir__versions" aria-label="Definition versions">
                {moveVersions.map((entry) => (
                  <li key={entry.version}><strong>{entry.version}</strong><span>{entry.when}</span><small>{entry.note}</small></li>
                ))}
              </ol>
              <footer><span>Definition · 4 versions</span><span>Illustrative example · not live data</span></footer>
            </div>
            <dl className="noir__facts">
              <div><dt>One handoff prompt</dt><dd>LoopGroup writes the prompt for the next agent: the loop’s identity, its recorded definition, the dependencies it needs, and how to keep reporting to the same loop. It never carries a key.</dd></div>
              <div><dt>Same loop, same history</dt><dd>Runs from the new agent land on the loop you already have. Reliability, rhythm, notes, and public cards continue without a gap.</dd></div>
              <div><dt>Performance by agent</dt><dd>See how the loop did on each agent with the sample size beside every number, so the decision to move rests on evidence. If the new agent does worse, moving back is the same prompt.</dd></div>
              <div><dt>Definitions, versioned</dt><dd>Every change to what the loop is told to do is recorded with a note. Compare any two versions line by line.</dd></div>
              <div><dt>Dependencies, by name</dt><dd>Tools, services, secrets, code, and models the loop needs, observed per agent. A move shows what the next agent still has to confirm. Values never travel.</dd></div>
            </dl>
          </div>
        </section>

        <section className="noir__groups" id="groups" aria-labelledby="groups-title">
          <div className="noir__section-head">
            <p className="noir__eyebrow">Groups</p>
            <h2 id="groups-title" className="noir__statement"><strong>Your team's loops, in one ledger.</strong> Share a loop into a group and everyone sees what runs, how reliably, and who owns it. A group never sees what you did not share.</h2>
          </div>
          <div className="noir__groups-grid">
            <div className="noir__group" aria-label="Illustrative group ledger">
              <header>
                <div><strong>Growth</strong><span>3 people · 4 shared loops</span></div>
                <div className="noir__members" aria-label="Members">{["AL", "SR", "JP"].map((initials) => <i key={initials}>{initials}</i>)}</div>
              </header>
              <div className="noir__group-rows">
                {groupLoops.map((loop) => (
                  <div className="noir__group-row" key={loop.name}>
                    <span className="noir__row-name"><ProviderMark provider={loop.provider} size={30} /><strong>{loop.name}</strong></span>
                    <span className="noir__owner"><i aria-hidden="true">{loop.initials}</i>{loop.owner}</span>
                    <span className="noir__health"><i aria-hidden="true" />{loop.reliability}<small> · {loop.resolved}</small></span>
                    <span className="noir__signal">{loop.lastSignal}</span>
                  </div>
                ))}
              </div>
              <footer>Illustrative example · not live data</footer>
            </div>
            <dl className="noir__facts">
              <div><dt>One loop, one group, reversible</dt><dd>Sharing is a grant you can revoke at any time. The loop and its history stay in your own workspace.</dd></div>
              <div><dt>Evidence, not secrets</dt><dd>The group sees names, cadence, outcomes, and reliability with its sample size. Never prompts, keys, conversations, or outputs.</dd></div>
              <div><dt>Agents get the context too</dt><dd>An agent connected to the group can list its loops and read group context over MCP, so it stops duplicating work already running elsewhere.</dd></div>
              <div><dt>Teams inside the group</dt><dd>Organize people and loops into teams like Growth or Finance Ops. Invitations are single-use links you send through your own channel.</dd></div>
            </dl>
          </div>
        </section>

        <section className="noir__share" id="share" aria-labelledby="share-title">
          <div className="noir__section-head">
            <p className="noir__eyebrow">Share</p>
            <h2 id="share-title" className="noir__statement"><strong>Post the card, not the screenshot.</strong> A public card is a link, an image, and a badge for the loops you are proud of. It shows what runs and how reliably. Never prompts, errors, cost, or keys.</h2>
          </div>
          <div className="noir__share-grid">
            <div className="noir__card" aria-label="Illustrative public status card">
              <header>
                <p className="noir__card-eyebrow"><i aria-hidden="true" />Signal ledger · status card</p>
                <h3>Ada’s loops</h3>
                <p className="noir__card-owner">Ada Lovelace · Sep 7, 2026</p>
              </header>
              <dl className="noir__card-stats">
                <div><dt>Loops</dt><dd>6</dd><small>status card</small></div>
                <div><dt>Runs this week</dt><dd>27</dd><small>25 resolved</small></div>
                <div><dt>Reliability</dt><dd>92%</dd><small>25 resolved</small></div>
              </dl>
              <div className="noir__card-rhythm" role="img" aria-label="Seven-day rhythm: 27 runs, 23 succeeded, 2 failed, 2 open">
                {cardRhythm.map((day) => {
                  const total = day.succeeded + day.failed + day.open;
                  return (
                    <div className="noir__card-day" key={day.label}>
                      <span className="noir__card-count">{total}</span>
                      <div className="noir__card-bar" style={{ height: `${total * 22}px` }}>
                        <i className="is-success" style={{ flex: day.succeeded }} />
                        {day.failed > 0 && <i className="is-failed" style={{ flex: day.failed }} />}
                        {day.open > 0 && <i className="is-open" style={{ flex: day.open }} />}
                      </div>
                      <span className="noir__card-label">{day.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="noir__card-rows">
                {cardLoops.map((loop) => (
                  <div className="noir__card-row" key={loop.name}>
                    <span className="noir__row-name"><ProviderMark provider={loop.provider} size={30} /><strong>{loop.name}</strong></span>
                    {loop.reliability ? <span className="noir__health"><i aria-hidden="true" />{loop.reliability}<small> · {loop.resolved}</small></span> : <span className="noir__card-withheld">— · reporting</span>}
                  </div>
                ))}
              </div>
              <footer><span>loopgroup.ai/c/…</span><span>Illustrative example · not live data</span></footer>
            </div>
            <dl className="noir__facts">
              <div><dt>One link, one image, one badge</dt><dd>Every card has a public page, a 1200×630 image for posts and link previews, and a small reliability badge for a README or a status page.</dd></div>
              <div><dt>Evidence only</dt><dd>Loop names, provider, cadence, evidence stage, reliability with its sample size, and the week’s rhythm. Descriptions travel only when you say so.</dd></div>
              <div><dt>Revoke it any time</dt><dd>A revoked card answers 404 on its page, image, and badge. The loops and their history stay in your workspace.</dd></div>
              <div><dt>Agents can publish too</dt><dd>A connected agent with manage scope can create a card over MCP, so the weekly status post can itself be one of your loops.</dd></div>
            </dl>
          </div>
        </section>

        <section className="noir__close" aria-labelledby="close-title">
          <p className="noir__eyebrow">Any agent</p>
          <h2 id="close-title" className="noir__display">Keep your loops.</h2>
          <p>Works with Grok Bot, Muse, dots, Claude, Codex, or any agent that can call MCP or HTTPS. It starts with a scan: nothing is edited or scheduled until you say so.</p>
          <div className="noir__marks" aria-hidden="true">
            {everyAgent.map((provider) => <ProviderMark key={provider} provider={provider} size={36} />)}
          </div>
          <Link className="noir__cta" to="/start">Plug in your first agent <ArrowRight size={18} aria-hidden="true" /></Link>
        </section>
      </main>

      <NoirFooter />
    </div>
  );
}
